import React, { useEffect, useMemo, useState } from "react";
import NavBar from "./NavBar";
import Footer from "./Footer";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FeedbackFab from "./FeedbackFab";
import LocationGate from "./LocationGate";
import { useLocationCtx } from "../context/LocationContext";
import CountryDebugBadge from "./debug/CountryDebugBadge";
import { useCountryGate } from "../hooks/useCountryGate";

const countryLanguageMap = {
  el: { lang: "el", languages: ["el", "en"] },
  en: { lang: "en", languages: ["en", "el"] },
  "en-us": { lang: "en-US", languages: ["en-US", "en", "el"] },
  "en-gb": { lang: "en-GB", languages: ["en-GB", "en", "el"] },
  ro: { lang: "ro", languages: ["ro", "en"] },
};

function getCountryFromPath(pathname) {
  const match = pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\b/);
  return match ? match[1] : "el";
}

const Layout = ({ children }) => {
  const location = useLocation();
  const country = getCountryFromPath(location.pathname);
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { needsCountrySelection, isAuthenticated } = useCountryGate();

  // Ύψος bottom nav για κινητά
  const bottomNavHeight = isMobile ? 68 : 0;

  // ✅ ΠΑΡΕ ΚΑΙ hydrated
  const {
    hydrated,
    guestLoaded,
    guestCountry,
    explicitUserSelectedCountry,
  } = useLocationCtx();

  const [locationGateOpen, setLocationGateOpen] = useState(false);

  const shouldAutoOpen = useMemo(
    () => hydrated && guestLoaded && needsCountrySelection,
    [hydrated, guestLoaded, needsCountrySelection]
  );

  useEffect(() => {
    if (!shouldAutoOpen) return;
    setLocationGateOpen(true);
    if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
      console.info("[LocationGateAuto] open", {
        guestLoaded,
        isAuthenticated,
        guestCountry,
        explicitUserSelectedCountry,
        needsCountrySelection,
      });
    }
  }, [shouldAutoOpen, guestLoaded, isAuthenticated, guestCountry, explicitUserSelectedCountry, needsCountrySelection]);

  useEffect(() => {
    if (process.env.REACT_APP_DEBUG_GUEST_STATE !== "1") return;
    console.info("[LocationGateAuto] state", {
      guestLoaded,
      isAuthenticated,
      guestCountry,
      explicitUserSelectedCountry,
      needsCountrySelection,
      forceOpen: locationGateOpen,
    });
  }, [guestLoaded, isAuthenticated, guestCountry, explicitUserSelectedCountry, needsCountrySelection, locationGateOpen]);

  useEffect(() => {
    const conf = countryLanguageMap[country] || countryLanguageMap["el"];
    if (!conf.languages.includes(i18n.language)) {
      i18n.changeLanguage(conf.lang);
    }
  }, [country, i18n]);

  // ✅ ΣΩΣΤΟ expression (χωρίς ;)

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      <NavBar onOpenLocationGate={() => setLocationGateOpen(true)} />

      <CountryDebugBadge />

      {/* ✅ Global location gate */}
      <LocationGate
        forceOpen={locationGateOpen}
        lockOpen={needsCountrySelection}
        onClose={() => setLocationGateOpen(false)}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          pb: { xs: `${bottomNavHeight}px`, md: 0 },
        }}
      >
        {children}
      </Box>

      {/* ✅ Feedback floating button (premium, global) */}
      <FeedbackFab bottomOffset={bottomNavHeight} />

      <Box component="footer" sx={{ pb: { xs: `${bottomNavHeight}px`, md: 0 } }}>
        <Footer id="app-footer" />
      </Box>
    </Box>
  );
};

export default Layout;
